import { Button, FlexWrapper, FontIcon, MovieDetails, Poster, RentTimer, Skeleton, Text } from '@intigral/shared';
import { MovieCard } from '@intigral/ui';
import { checkShowLockIcon, isSubscriptionLockedContent, showUpgradeOrOpenDetail } from '../../helpers/vod';
import { __ } from '../../translations';
import AddWatchlistButton from '../elements/AddWatchlistButton';
import { ButtonTypes, COMPONENT_TYPES } from '@libs/constants';
import h, { isTVodProgram } from '../../helpers/common';
import { appConfig } from '../../config/app.config';
import { useDispatch } from 'react-redux';
import { useContext, useState } from 'react';
import useTimeToExpire from '../../hooks/useTimeToExpire';
import { useReminderItem } from '../../hooks/useReminderItem';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import playerService from '../../helpers/bitmovinPlayer';
import { trackCarouselItemDetailClick } from '../../helpers/trackCarouselItemDetailClick';
import { useUserReminders } from '../../store/reducers/reminderSlice';
import { getUserInfo } from '../../utils/user';
import { showUpgradedModal } from '../../store/reducers/generalConfigurationSlice';
import _ from 'lodash';
import { Modal } from './Modal';
import { useConfiguration } from '../../hooks/useConfiguration';
import { useTvod } from '../../hooks/useTvod';
import { MoviePlayButton } from '../elements/MoviePlayButton';

const RenderItem = ({ movie, newConfig, openDetailOverlay, reminderData, enableLockIcon }) => {
    const dispatch = useDispatch();
    const { track = () => undefined } = useContext(MixpanelContext) || {};

    const [isMuted, setIsMuted] = useState(true);
    const { priceInfo, isTvod, expiryDate, isRented } = useTvod(movie);
    const { priceWithVat = '', currency = '' } = priceInfo || {};

    const { hours, minutes, durationInMinutes } = useTimeToExpire(expiryDate);
    const { isComingSoonItem, isReminded } = useReminderItem(movie, reminderData ?? []);
    const isLocked = checkShowLockIcon(movie?.subscriptionGuids ?? []) && enableLockIcon;
    const locked = (!isTvod && isLocked) || movie.isLocked;
    const isSubscriptionLocked = isSubscriptionLockedContent(movie, locked);
    let tvodRentNowBtnText = __('tvod_rent_now_button_details') || '';
    if (priceWithVat) {
        tvodRentNowBtnText = tvodRentNowBtnText.replace('{priceWithVat}', `<label  dir="ltr"> ${priceWithVat}`);
    } else {
        tvodRentNowBtnText = tvodRentNowBtnText.replace(' - {priceWithVat}', ``);
    }
    tvodRentNowBtnText = tvodRentNowBtnText.replace('{currency}', `${currency}</label>`);
    tvodRentNowBtnText = tvodRentNowBtnText.replace(/<label(.*?)>/g, '<span$1>').replace(/<\/label>/g, '</span>');

    const formatTvodPrice = (priceWithVat, currency, isRented) => {
        if (isRented) {
            return __('tvod_poster_rented');
        }
        return `${priceWithVat} ${currency}`;
    };

    const openVodDetailOverlay = () => {
        if (movie?.useCaseId) {
            playerService.setUseCaseId(movie.useCaseId);
        }
        openDetailOverlay(movie);
    };

    const handleCardClick = () => {
        trackCarouselItemDetailClick({
            track,
            item: movie,
            componentType: COMPONENT_TYPES.IMAGES_CAROUSEL,
            componentName: COMPONENT_TYPES.IMAGES_CAROUSEL,
            meta: {},
        });
        showUpgradeOrOpenDetail({ item: movie, locked, dispatch, openDetail: openVodDetailOverlay });
    };

    return (
        <MovieCard
            key={movie.Id + 1}
            id={movie?.Id + 1}
            cover={
                movie.image
                    ? h.getImagizerUrl(movie.image, newConfig.imagizer?.components_images.VOD_LIST_ITEM)
                    : appConfig.defaultPortraitImage
            }
            picture={<img src={h.getImagizerUrl(movie?.cover)} alt="" />}
            info={[
                ...(movie.tags && Array.isArray(movie.tags)
                    ? movie.tags.filter((g) => g.id.startsWith('id_Genre_')).map((g) => __(g.title))
                    : []),
            ]}
            heading={movie.title}
            page={
                isSubscriptionLocked
                    ? undefined
                    : {
                          onClick: (e) => {
                              e.stopPropagation();
                              openVodDetailOverlay();
                          },
                      }
            }
            watchlistButton={
                <AddWatchlistButton
                    item={{ Id: movie.id, programType: movie.programType }}
                    type={ButtonTypes.VodeItem}
                    componentType={COMPONENT_TYPES.IMAGES_CAROUSEL}
                    componentName={COMPONENT_TYPES.IMAGES_CAROUSEL}
                />
            }
            play={
                isComingSoonItem ? null : !movie.isLocked && locked ? (
                    <Button onClick={() => dispatch(showUpgradedModal(true))} data-test-id="play-button">
                        {__('play_now')} <FontIcon name="lock" size="smaller" md="smaller" />
                    </Button>
                ) : (
                    <MoviePlayButton
                        isTvod={isTvod}
                        isRented={isRented}
                        item={movie}
                        componentName={COMPONENT_TYPES.IMAGES_CAROUSEL}
                        componentType={COMPONENT_TYPES.IMAGES_CAROUSEL}
                        tvodRentText={tvodRentNowBtnText}
                        openDetailOverlay={openDetailOverlay}
                        hours={hours}
                        minutes={minutes}
                        meta={undefined}
                        locked={undefined}
                        isComingSoonItem={undefined}
                    />
                )
            }
            onClick={handleCardClick}
            price={isTvod && priceWithVat && formatTvodPrice(priceWithVat, currency, isRented)}
            timer={
                isTvod &&
                isRented && (
                    <RentTimer
                        timeCount={hours > 0 ? [hours, 'hour'] : [minutes, 'minute']}
                        timeType={
                            hours > 0
                                ? __('tvod_rent_expiration_hours')
                                : minutes > 0
                                ? __('tvod_rent_expiration_minutes')
                                : ''
                        }
                        labelRemaining={__('tvod_rent_remaining_time')}
                        {...(hours < 0 && minutes < 0 ? { expired: true } : {})}
                        expiredLabel={__('tvod_rent_expired')}
                        {...(durationInMinutes < Number(h.getTVodConfig().diffExpirationTimeInMinutes)
                            ? { almostEnded: true }
                            : {})}
                    />
                )
            }
            {...(isLocked && !isTVodProgram(movie) && { icon: 'locked' })}
            {...(locked ? { icon: 'locked' } : {})}
            {...(isComingSoonItem && isReminded ? { icon: 'remind' } : {})}
        />
    );
};

const CastCrewModal = ({ isModalOpened, modalContent, closeModal, openDetailOverlay, enableLockIcon }) => {
    const { data: newConfig } = useConfiguration();
    const isEmptyModalContent =
        !modalContent || (typeof modalContent === 'object' && Object.keys(modalContent).length === 0);
    const userInfo = getUserInfo();

    const { data: reminderData } = useUserReminders({ detailed: false }, userInfo);

    return (
        <Modal useFlexLayout={false} open={isModalOpened} onOpenChange={closeModal}>
            {isEmptyModalContent && (
                <>
                    <FlexWrapper flexDirection="row" gap="4x" alignItems="center" marginBottom="4x">
                        <Skeleton width={186} height={280} />
                        <FlexWrapper flexDirection="column" gap="2x">
                            <Skeleton width={100} height={24} />
                            <Skeleton width={50} />
                        </FlexWrapper>
                    </FlexWrapper>
                    <Skeleton width="100%" height={200} />
                </>
            )}
            {!isEmptyModalContent && (
                <>
                    <FlexWrapper flexDirection="row" gap="4x" alignItems="end" marginBottom="4x">
                        <Poster inline width={256} picture={modalContent?.image} alt={modalContent?.title} />
                        <Text as="h2" variant="h2" alignment="start" margin="0">
                            {modalContent?.title}
                            <MovieDetails
                                info={
                                    Array.isArray(modalContent?.position)
                                        ? modalContent?.position?.map((pos) => {
                                              return __(`cast_crew_${(pos.trim() || '').toLowerCase()}`);
                                          })
                                        : [__(`cast_crew_${(modalContent?.position?.trim() || '').toLowerCase()}`)]
                                }
                            />
                        </Text>
                    </FlexWrapper>
                    <Text as="h3" variant="h3" size="24" alignment="start" style={{ letterSpacing: '.01em' }}>
                        {__('filmography')}
                    </Text>
                    <FlexWrapper itemCount={5} gap="2x">
                        {(modalContent?.filmography ?? []).map((movie) => {
                            // const isLocked = checkShowLockIcon(movie?.id_Categories_TVOD ?? []) && enableLockIcon;
                            return (
                                <RenderItem
                                    key={movie.Id}
                                    movie={movie}
                                    openDetailOverlay={openDetailOverlay}
                                    newConfig={newConfig}
                                    reminderData={reminderData}
                                    enableLockIcon={enableLockIcon}
                                />
                            );
                        })}
                    </FlexWrapper>
                </>
            )}
        </Modal>
    );
};

export default CastCrewModal;
